import { useSuspenseQuery } from '@tanstack/react-query';
import * as React from 'react';

import type * as api from '@gitbook/api-client';
import { formatComputedIntegrationSourceType } from '@gitbook/api-client/src/computed';
import { ContentKitLifecycle, type ContentKitRemoteInput } from '@gitbook/contentkit-react';
import { webConfig } from '@gitbook/env';
import { useThemeVariants } from '@gitbook/react-uikit';
import { LoadingPanel } from '@gitbook/spine-react';
import { assert } from '@gitbook/util-ts';

import { apiQuery, useContentKitRemote } from '../../../state';
import { contentKitRemoteComponents } from '../../ContentKit';

export interface ConfigureContentIntegrationSourceFlowProps {
    /** Organization in which the flow is being done */
    organizationId: string;
    /** Optional space ID in which the flow is being done */
    spaceId?: string;
    /** Integration owning the content source */
    integration: api.Integration;
    /** Content source to configure */
    contentSource: api.IntegrationContentSource;
    /** Callback when the configuration is done. */
    onSubmit: (source: api.ComputedContentSourceIntegration) => void;
    /** Input to initiliaze configuration. */
    contentSourceInput?: {
        props: api.PlainObject;
        dependencies: api.PlainObject;
    };
    /**
     * Label for the button to submit the configuration.
     */
    submitLabel?: string;
}

export function ConfigureContentIntegrationSourceFlowByPage(
    props: Pick<
        ConfigureContentIntegrationSourceFlowProps,
        'organizationId' | 'spaceId' | 'onSubmit'
    > & {
        page: api.RevisionPageComputed;
        integrationName: string;
        contentSourceId: string;
    }
) {
    const { organizationId, spaceId, page, onSubmit, integrationName, contentSourceId } = props;

    const { data: integration } = useSuspenseQuery(
        apiQuery('getIntegrationByName', { integrationName })
    );

    const contentSource = React.useMemo(() => {
        return integration.contentSources?.find((source) => source.id === contentSourceId);
    }, [integration, contentSourceId]);

    assert(
        contentSource,
        `Content source with ID "${contentSourceId}" not found in integration "${integrationName}"`
    );

    const contentSourceInput = React.useMemo(() => {
        return {
            props: page.computed.props,
            dependencies: page.computed.dependencies,
        };
    }, [page.computed.props, page.computed.dependencies]);

    return (
        <ConfigureContentIntegrationSourceFlow
            organizationId={organizationId}
            spaceId={spaceId}
            integration={integration}
            contentSource={contentSource}
            onSubmit={onSubmit}
            contentSourceInput={contentSourceInput}
            submitLabel="Save changes"
        />
    );
}

/**
 * Flow inside a modal to configure a specific content source.
 */
export function ConfigureContentIntegrationSourceFlow(
    props: ConfigureContentIntegrationSourceFlowProps
) {
    const {
        organizationId,
        spaceId,
        integration,
        contentSource,
        contentSourceInput,
        onSubmit,
        submitLabel,
    } = props;
    const integrationBlocksRemote = useContentKitRemote();
    const { color } = useThemeVariants();

    const componentId = contentSource.configuration.componentId;
    const input = React.useMemo<ContentKitRemoteInput>(() => {
        return {
            integrationId: integration.name,
            componentId,
            props: {
                contentSource: contentSourceInput,
                submitLabel,
            },
            context: {
                type: 'configuration_contentsource',
                organizationId,
                spaceId,
                theme: color,
            },
        };
    }, [
        integration.name,
        componentId,
        organizationId,
        spaceId,
        color,
        contentSourceInput,
        submitLabel,
    ]);

    return (
        <React.Suspense fallback={<LoadingPanel size="large" />}>
            <ContentKitLifecycle
                mode="configuration"
                remote={integrationBlocksRemote}
                remoteComponents={contentKitRemoteComponents}
                input={input}
                security={{
                    firstPartyDomains: [webConfig.integrations.domain],
                }}
                onComplete={(result) => {
                    onSubmit({
                        ...(result as Pick<
                            api.ComputedContentSourceIntegration,
                            'props' | 'dependencies'
                        >),
                        type: formatComputedIntegrationSourceType({
                            category: 'integration',
                            integration: integration.name,
                            contentSourceId: contentSource.id,
                        }),
                    });
                }}
            />
        </React.Suspense>
    );
}
